import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, MapPin, X, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const REASON = { sos: "SOS manuel", fall: "Détection de chute", deadman: "Absence de mouvement", timeout: "Absence de confirmation" };
const fmt = (s) => { try { return new Date(s).toLocaleString("fr-FR"); } catch { return s || ""; } };

export default function PTIAlertBanner() {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const seen = useRef(new Set());
  const dismissed = useRef(new Set());
  const first = useRef(true);
  const audioCtx = useRef(null);
  const beepTimer = useRef(null);

  const startAlarm = useCallback(() => {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      if (ctx.state === "suspended") ctx.resume();
      const beep = () => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "square"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        o.connect(g); g.connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 0.4);
      };
      beep();
      if (beepTimer.current) clearInterval(beepTimer.current);
      beepTimer.current = setInterval(beep, 900);
    } catch { /* audio bloqué par le navigateur */ }
  }, []);

  const stopAlarm = useCallback(() => {
    if (beepTimer.current) { clearInterval(beepTimer.current); beepTimer.current = null; }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pti/alerts");
      const open = (data || []).filter((a) => a.status === "ouverte");
      if (first.current) { open.forEach((a) => seen.current.add(a.pti_alert_id)); first.current = false; return; }
      const fresh = open.find((a) => !seen.current.has(a.pti_alert_id) && !dismissed.current.has(a.pti_alert_id));
      open.forEach((a) => seen.current.add(a.pti_alert_id));
      setAlert((cur) => { if (!cur && fresh) { startAlarm(); return fresh; } return cur; });
    } catch { /* ignore */ }
  }, [startAlarm]);

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => () => stopAlarm(), [stopAlarm]);

  const close = () => { if (alert) dismissed.current.add(alert.pti_alert_id); stopAlarm(); setAlert(null); };
  const openPti = () => { close(); navigate("/dashboard/pti"); };

  if (!alert) return null;
  const gps = alert.lat != null && alert.lng != null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="pti-alert-banner">
      <div className="absolute inset-0 animate-pulse bg-[#FF3B30]/20 pointer-events-none" />
      <div className="relative w-[92%] max-w-md rounded-2xl border-2 border-[#FF3B30] bg-[#161616] p-6 shadow-2xl">
        <button onClick={close} data-testid="pti-alert-close" className="absolute top-3 right-3 text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        <div className="flex flex-col items-center text-center gap-2">
          <div className="w-16 h-16 rounded-full bg-[#FF3B30]/15 flex items-center justify-center animate-pulse"><ShieldAlert className="w-9 h-9 text-[#FF3B30]" /></div>
          <h2 className="text-xl font-bold text-white mt-1">🆘 Alerte PTI</h2>
          <p className="text-[#FF6b60] font-semibold" data-testid="pti-alert-reason">{REASON[alert.reason] || "Alerte"}</p>
          <p className="text-white text-lg font-medium" data-testid="pti-alert-agent">{alert.agent_name || "Agent"}</p>
          <p className="text-sm text-zinc-400">Site : {alert.site_nom || "Non renseigné"}</p>
          <p className="text-xs text-zinc-500">{fmt(alert.created_at)}</p>
          {gps && <a href={`https://www.google.com/maps?q=${alert.lat},${alert.lng}`} target="_blank" rel="noreferrer" className="text-xs text-[#4aa3ff] flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> Voir la position GPS</a>}
        </div>
        <div className="flex gap-2 mt-5">
          <Button onClick={openPti} data-testid="pti-alert-open" className="flex-1 bg-[#FF3B30] hover:bg-[#ff5a50] text-white"><BellRing className="w-4 h-4 mr-1.5" /> Ouvrir le tableau PTI</Button>
          <Button onClick={close} variant="outline" data-testid="pti-alert-dismiss" className="bg-transparent border-zinc-700 text-white hover:bg-zinc-900">Fermer</Button>
        </div>
      </div>
    </div>
  );
}
